import { GridColDef } from "@mui/x-data-grid-pro";
import PaymentMehtodType from "../../common/components/paymentmethod.type";
import { Button } from "@mui/material";
import { Transaction } from "../../domain/transactions/type";
import { PaymentStatus } from "../../types/enums";
import SummarizeIcon from "@mui/icons-material/Summarize";
import { ProcessingMethodLabels } from "../../mappings/processing-method.mapping";
import {
  IsActionColumnVisibleForTransactionsPage,
  formatNumber,
  isSurchargeEnabled,
  resolvePaymentStatusColor
} from "../../common/components/AppUtils";
import { GetDealerPlatformFeeOption } from "../../utils/UtilTarget";
import moment from "moment";
import {
  dateOperators,
  numericOperators,
  stringOperators
} from "../../mappings/mui-grid-operators-mapping";
import { useMemo } from "react";

export interface CustomFieldLabel {
  name: string;
  label: string;
  direction?: string;
}

/**
 * Renders a cell for amount columns, showing red and parentheses for refunded/cancelled.
 *
 * Formats via the shared `formatNumber` (AppUtils) so on-screen amounts always show
 * exactly 2 decimal places, matching the convention already used by the pinned Totals
 * row and the Excel export in this same grid. `formatNumber` is a no-op passthrough for
 * values it can't parse as numeric (e.g. the Totals row's pre-formatted "$1,234.56"
 * strings), so it's safe to apply unconditionally here. `param.value ?? 0` guards against
 * a missing amount rendering as the literal text "undefined"/"NaN".
 */
export const renderAmountCell = (param: any) => {
  if (
    param.row.status === PaymentStatus.Refunded ||
    param.row.status === PaymentStatus.RefundFailed ||
    param.row.status === PaymentStatus.Cancelled ||
    param.row.status === PaymentStatus.AutoReversal
  ) {
    return (
      <label style={{ color: "red" }}>
        ({formatNumber(Math.abs(param.value ?? 0))})
      </label>
    );
  }
  return formatNumber(param.value ?? 0);
};

/**
 * Generates column definitions for the transactions DataGrid.
 *
 * @param onVoidClick - Callback triggered when the "Void" button is clicked for a row.
 * @param customFieldLabels - Array of custom field definitions to generate dynamic columns.
 * @returns An array of column definitions to be used in MUI DataGrid.
 */
export const useTransactionsColumns = (
  onVoidClick: (event: React.MouseEvent<HTMLElement>, row: Transaction) => void,
  customFieldLabels: CustomFieldLabel[] = []
): GridColDef[] => {
  /**
   * Create dynamic columns for custom fields.
   * Separate into start and end columns based on direction property.
   */
  const { startColumns, endColumns } = useMemo(() => {
    const createColumn = (field: CustomFieldLabel): GridColDef => ({
      field: `${field.name}::${field.label}`,
      headerName: field.label,
      minWidth: 150,
      sortable: true,
      filterable: true,
      filterOperators: stringOperators,
      pinnable: false,
      renderCell: (params: any) => {
        if (params.id === "totals") return "";
        return <span>{params.value}</span>;
      }
    });

    const start = customFieldLabels
      .filter(field => field.direction === "Start")
      .map(createColumn);

    const end = customFieldLabels
      .filter(field => field.direction === "End")
      .map(createColumn);

    return { startColumns: start, endColumns: end };
  }, [customFieldLabels]);

  const baseColumns: GridColDef[] = [
    /**
     * Column for displaying invoice/order ID.
     * If the row is a "totals" row, a custom summary cell is rendered instead.
     */
    {
      field: "invoiceId",
      headerName: "Order#",
      minWidth: 120,
      sortable: true,
      filterable: true,
      filterOperators: stringOperators,
      pinnable: false,
      renderCell: param => {
        if (param.id === "totals") {
          return (
            <span
              style={{
                fontSize: "20px",
                display: "flex",
                alignItems: "center"
              }}
            >
              <>
                <SummarizeIcon fontSize="medium" style={{ marginRight: 4 }} />
                Total
              </>
            </span>
          );
        }
        return <span>{param.value}</span>;
      }
    },

    /** Reference/payment ID column */
    {
      field: "paymentId",
      headerName: "Reference",
      minWidth: 210,
      flex: 1,
      sortable: true,
      filterable: true,
      filterOperators: stringOperators,
      pinnable: false
    },

    /** Dealer location column */
    {
      field: "dealerLocation",
      headerName: "Location",
      minWidth: 215,
      flex: 1,
      sortable: true,
      filterable: true,
      filterOperators: stringOperators,
      pinnable: false
    },

    /** Customer name column */
    {
      field: "customerName",
      headerName: "Customer",
      minWidth: 140,
      flex: 1,
      sortable: true,
      filterable: true,
      filterOperators: stringOperators,
      pinnable: false
    },

    /** Transaction amount column with localized formatting */
    {
      field: "transactionAmount",
      headerName: "Amount",
      type: "number",
      minWidth: 130,
      flex: 1,
      sortable: true,
      filterable: true,
      filterOperators: numericOperators,
      pinnable: false,
      valueFormatter: ({ value }) => (value ?? 0).toLocaleString(),
      renderCell: renderAmountCell
    },

    /** Surcharge column */
    {
      field: "surcharge",
      headerName: "Surcharge",
      type: "number",
      minWidth: 145,
      flex: 1,
      sortable: true,
      filterable: false,
      hideable: isSurchargeEnabled(),
      pinnable: false,
      valueFormatter: ({ value }) => (value ?? 0).toLocaleString(),
      renderCell: renderAmountCell
    },

    /** Platform fee column */
    {
      field: "platformFee",
      headerName: "Platform fee",
      type: "number",
      minWidth: 160,
      flex: 1,
      sortable: true,
      filterable: false,
      hideable: GetDealerPlatformFeeOption(),
      pinnable: false,
      valueFormatter: ({ value }) => (value ?? 0).toLocaleString(),
      renderCell: renderAmountCell
    },

    /** Total transaction amount column */
    {
      field: "totalAmount",
      headerName: "Total",
      type: "number",
      minWidth: 110,
      flex: 1.5,
      sortable: true,
      filterable: false,
      filterOperators: numericOperators,
      pinnable: false,
      hideable: isSurchargeEnabled() || GetDealerPlatformFeeOption(),
      renderCell: renderAmountCell
    },

    /** Transaction status column rendered with colored status chips */
    {
      field: "status",
      headerName: "Status",
      minWidth: 125,
      flex: 1.5,
      sortable: true,
      filterable: true,
      filterOperators: stringOperators,
      pinnable: false,
      renderCell: params => {
        if (params.id === "totals") {
          return null; // 👈 Don't render anything for totals row
        }

        return (
          <label
            style={{
              color: resolvePaymentStatusColor(
                params.row.status,
                params.row.transactionTypeId
              )
            }}
          >
            {params.row.status}
          </label>
        );
      }
    },

    /** Transaction date column formatted in UTC */
    {
      field: "transactionDate",
      headerName: "Transaction Date",
      type: "date",
      minWidth: 190,
      flex: 1,
      sortable: true,
      filterable: false,
      filterOperators: dateOperators,
      pinnable: false,
      valueFormatter: param => {
        if (param.id === "totals") {
          return ""; // 👈 Don't render anything for totals row
        }
        return moment
          .utc(param.value)
          .local()
          .format("M/DD/YYYY hh:mm A");
      }
    },

    /** Transaction date column formatted in UTC */
    {
      field: "refundedDate",
      headerName: "Refunded Date",
      type: "date",
      minWidth: 180,
      flex: 1,
      sortable: true,
      filterable: true,
      filterOperators: dateOperators,
      pinnable: false,
      valueFormatter: param => {
        if (param.id === "totals") {
          return ""; // 👈 Don't render anything for totals row
        }
        return param.value
          ? moment
              .utc(param.value)
              .local()
              .format("M/DD/YYYY hh:mm A")
          : null;
      }
    },

    /** Processing method column (mapped from enum labels) */
    {
      field: "paymentType",
      headerName: "Processing Method",
      flex: 1.5,
      minWidth: 210,
      sortable: true,
      filterable: true,
      filterOperators: stringOperators,
      pinnable: false,
      renderCell: param => ProcessingMethodLabels[param.value] ?? param.value
    },

    /** Masked card number column */
    {
      field: "cardLastFourNumber",
      headerName: "Ending In",
      type: "number",
      flex: 1.5,
      minWidth: 139,
      sortable: true,
      filterable: true,
      filterOperators: stringOperators,
      pinnable: false
    },

    /** Card type column rendered using custom PaymentMethodType component */
    {
      field: "transactionType",
      headerName: "Card",
      flex: 1.5,
      minWidth: 110,
      sortable: true,
      filterable: false,
      filterOperators: stringOperators,
      pinnable: false,
      renderCell: param => PaymentMehtodType({ type: param.value })
    },

    /**
     * Action column rendering a "Void" button.
     * Button is shown only if:
     * - The transaction succeeded and is not refunded/cancelled, OR
     * - Transaction is refunded but has remaining refundable amount.
     */
    {
      field: "action",
      headerName: "Action",
      flex: 1.5,
      minWidth: 100,
      filterable: false,
      sortable: false,
      hideable: IsActionColumnVisibleForTransactionsPage(),
      pinnable: false,
      renderCell: param => {
        if (param.id === "totals") {
          return null; // 👈 Don't render anything for totals row
        }
        const row = param.row;
        if (
          (row.status === PaymentStatus.Success &&
            row.totalAmount > 0 &&
            !row.isRefunded &&
            !row.isCancelled &&
            !row.refundInitiated) ||
          (row.status === PaymentStatus.Refunded &&
            row.totalAmount - row.refundedAmount > 0 &&
            !row.refundInitiated &&
            row.transactionTypeId !== 1) ||
          (row.status === PaymentStatus.Success &&
            row.totalAmount > 0 &&
            row.totalAmount - row.refundedAmount > 0)
        ) {
          return (
            <Button
              variant="outlined"
              color="error"
              onClick={e => onVoidClick(e, param.row)}
              style={{ textTransform: "none", padding: "00px" }}
            >
              Void
            </Button>
          );
        }
        return null;
      }
    }
  ];

  // Merge custom field columns: start at beginning, end before action column
  return useMemo(() => {
    if (startColumns.length === 0 && endColumns.length === 0) {
      return baseColumns;
    }

    // Action column is the last column in baseColumns
    const actionColumn = baseColumns[baseColumns.length - 1];
    const columnsWithoutAction = baseColumns.slice(0, -1);

    return [
      ...startColumns,
      ...columnsWithoutAction,
      ...endColumns,
      actionColumn
    ];
  }, [startColumns, endColumns, baseColumns]);
};
